import { DataRow } from "@/api/types";
import {
  Column,
  Filter,
  OnSearchFilterSortPaginate,
  Pagination,
  Search,
  Sort,
  SortOrder,
} from "@/components/Table/Table";
import { DEFAULT_PAGINATION } from "@/constants";
import { useSearchParams } from "react-router-dom";

function parseObjectFromURLSearch<R = DataRow>(
  columns: Column<R>[],
  searchParams: URLSearchParams,
  listKey: string,
  parser?: (value: string) => any
) {
  const parsed: Record<string, any> = {};

  columns.forEach((column) => {
    const columnSearch = searchParams.get([listKey, column.key].join("_"));
    if (columnSearch) {
      parsed[column.key] = parser?.(columnSearch) || columnSearch;
    }
  });

  return parsed;
}

function createURLSearchFromObject(
  parsed: Record<string, any>,
  listKey: string,
  parser?: (value: any) => string
) {
  const searchParams: Record<string, { key: string; value: any }> = {};
  for (const [key, value] of Object.entries(parsed)) {
    searchParams[key] = { key: [listKey, key].join("_"), value: parser?.(value) || value };
  }
  return searchParams;
}

export default function useTableStateUrl<R = DataRow>({
  columns,
  defaultSort,
  defaultPagination,
}: {
  columns: Column<R>[];
  defaultSort?: Sort;
  defaultPagination?: Pagination;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = searchParams.get("page") && Number(searchParams.get("page"));
  const pageSize = searchParams.get("pageSize") && Number(searchParams.get("pageSize"));
  const pagination: Pagination =
    page && pageSize ? { page, pageSize } : defaultPagination || DEFAULT_PAGINATION;

  const sortOrder: SortOrder | null =
    searchParams.get("sortOrder") === "ascend"
      ? "ascend"
      : searchParams.get("sortOrder") === "descend"
        ? "descend"
        : null;
  const sort: Sort | undefined =
    searchParams.get("sortKey") !== null && sortOrder !== null
      ? { key: searchParams.get("sortKey") || "", order: sortOrder }
      : defaultSort;

  const search: Search = parseObjectFromURLSearch(columns, searchParams, "search");
  const filter: Filter = parseObjectFromURLSearch(
    columns,
    searchParams,
    "filter",
    (value: string) => value.split(";")
  );

  const clear = () => {
    onSearchFilterSortPaginate({
      pagination: { ...pagination, page: 1 },
      filter: {},
      search: {},
      sort: null,
    });
  };

  const onSearchFilterSortPaginate: OnSearchFilterSortPaginate = ({
    pagination: newPagination,
    filter: newFilter,
    search: newSearch,
    sort: newSort,
  }) => {
    if (newPagination) {
      searchParams.set("page", newPagination.page.toString());
      searchParams.set("pageSize", newPagination.pageSize.toString());
    }

    if (newSort) {
      searchParams.set("sortOrder", newSort.order);
      searchParams.set("sortKey", newSort.key);
    } else if (newSort === null) {
      // don't delete them, otherwise it will go back to default
      searchParams.set("sortOrder", "ascend");
      searchParams.set("sortKey", "");
    }

    if (newFilter) {
      const parsed = createURLSearchFromObject(newFilter, "filter", (value: string[]) =>
        value.join(";")
      );
      columns.forEach((column) => {
        const parsedValue = parsed?.[column.key];
        if (parsedValue) {
          searchParams.set(parsedValue.key, parsedValue.value);
        } else {
          searchParams.delete(["filter", column.key].join("_"));
        }
      });
    }

    if (newSearch) {
      const parsed = createURLSearchFromObject(newSearch, "search");
      columns.forEach((column) => {
        const parsedValue = parsed?.[column.key];
        if (parsedValue) {
          searchParams.set(parsedValue.key, parsedValue.value);
        } else {
          searchParams.delete(["search", column.key].join("_"));
        }
      });
    }

    setSearchParams(searchParams);
  };

  const setPage = (page: number) => {
    onSearchFilterSortPaginate({ pagination: { ...pagination, page } });
  };

  return { pagination, filter, search, sort, onSearchFilterSortPaginate, clear, setPage };
}
